import React, { useMemo } from "react";
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    type NodeTypes,
    type OnNodesChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./explorer.css";
import BomLinesListNode from "./nodes/BomLinesListNode";
import CustomerPaymentsNode from "./nodes/CustomerPaymentsNode";
import EntityNode from "./nodes/EntityNode";
import FMRouteNode from "./nodes/FMRouteNode";
import SalesOrderLinesNode from "./nodes/SalesOrderLinesNode";
import ServiceOrderLinesNode from "./nodes/ServiceOrderLinesNode";
import LineDetailNode from "./nodes/LineDetailNode";
import ReleasedProductListNode from "./nodes/ReleasedProductListNode";
import RouteOperationsListNode from "./nodes/RouteOperationsListNode";
import OperationDetailNode from "./nodes/OperationDetailNode";
import type { ExplorerNode, ExplorerEdge } from "../declarations/IExplorerTypes";
import { useTheme } from "@mui/material/styles";
import { getNodeColor } from "./nodes/nodeColors";
import type { ExplorerEntityType } from "../declarations/IExplorerTypes";
import { useExplorerStore } from "../stores/useExplorerStore";
interface IExplorerGraphProps {
    edges: ExplorerEdge[];
    nodes: ExplorerNode[];
    onNodesChange?: OnNodesChange<ExplorerNode>;
}

const nodeTypes: NodeTypes = {
    entityNode: EntityNode,
    salesOrderLinesNode: SalesOrderLinesNode,
    serviceOrderLinesNode: ServiceOrderLinesNode,
    lineDetailNode: LineDetailNode,
    releasedProductListNode: ReleasedProductListNode,
    routeOperationsListNode: RouteOperationsListNode,
    operationDetailNode: OperationDetailNode,
    bomLinesListNode: BomLinesListNode,
    fmRouteNode: FMRouteNode,
    customerPaymentsNode: CustomerPaymentsNode,
};

/** Map an edge's target node ID to an entity type for color-matching */
function inferEntityType(targetId: string): ExplorerEntityType {
    if (targetId.startsWith("SalesOrderLineDetail-")) return "SalesOrderLineDetail";
    if (targetId.startsWith("ReleasedProducts-")) return "ReleasedProduct";
    if (targetId.startsWith("ReleasedProduct-")) return "ReleasedProduct";
    if (targetId.startsWith("Product-")) return "Product";
    if (targetId.startsWith("Routes-")) return "Route";
    if (targetId.startsWith("OpDetail-")) return "RouteOperationDetail";
    if (targetId.startsWith("BomLines-")) return "BomLines";
    if (targetId.startsWith("FMRoute-")) return "FMRoute";
    if (targetId.startsWith("Vendor-")) return "Vendor";
    const map: Record<string, ExplorerEntityType> = {
        SalesOrderHeader: "SalesOrderHeader",
        Customer: "Customer",
        VatNum: "VatNum",
        SalesOrderHeaderCharge: "SalesOrderHeaderCharge",
        SalesOrderLines: "SalesOrderLines",
        CustomerPayments: "CustomerPayments",
        Product: "Product",
    };
    return map[targetId] ?? "SalesOrderHeader";
}

/** Directional BFS: ancestors (target→source) + descendants (source→target) */
function getPathNodeIds(selectedId: string, edges: ExplorerEdge[]): Set<string> {
    const forwardMap = new Map<string, string[]>();  // source → targets
    const reverseMap = new Map<string, string[]>();  // target → sources
    for (const edge of edges) {
        if (!forwardMap.has(edge.source)) forwardMap.set(edge.source, []);
        forwardMap.get(edge.source)!.push(edge.target);
        if (!reverseMap.has(edge.target)) reverseMap.set(edge.target, []);
        reverseMap.get(edge.target)!.push(edge.source);
    }
    const path = new Set<string>([selectedId]);
    // Traverse ancestors (upstream)
    const ancestorQueue = [selectedId];
    while (ancestorQueue.length > 0) {
        const current = ancestorQueue.shift()!;
        for (const parent of reverseMap.get(current) ?? []) {
            if (!path.has(parent)) {
                path.add(parent);
                ancestorQueue.push(parent);
            }
        }
    }
    // Traverse descendants (downstream)
    const descendantQueue = [selectedId];
    while (descendantQueue.length > 0) {
        const current = descendantQueue.shift()!;
        for (const child of forwardMap.get(current) ?? []) {
            if (!path.has(child)) {
                path.add(child);
                descendantQueue.push(child);
            }
        }
    }
    return path;
}

export default function ExplorerGraph({ nodes, edges, onNodesChange }: IExplorerGraphProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const selectedNodeId = useExplorerStore((s) => s.selectedNodeId);

    /** Nodes that sit to the LEFT of SalesOrderHeader — edges flow right-to-left */
    const leftSideSourceNodes = new Set(["SalesOrderHeaderCharge", "CustomerPayments", "Customer", "VatNum"]);

    const connectedNodeIds = useMemo(() => {
        if (!selectedNodeId) return null;
        return getPathNodeIds(selectedNodeId, edges);
    }, [selectedNodeId, edges]);

    const styledEdges = useMemo(() => {
        return edges.map((edge) => {
            const entityType = inferEntityType(edge.target);
            const color = getNodeColor(entityType);
            const isReverseFlow = leftSideSourceNodes.has(edge.source);
            const isOnPath = !connectedNodeIds
                || (connectedNodeIds.has(edge.source) && connectedNodeIds.has(edge.target));
            return {
                ...edge,
                style: {
                    stroke: color,
                    strokeWidth: isOnPath ? 1.8 : 1.2,
                    strokeOpacity: isOnPath ? 0.55 : 0.12,
                    transition: "stroke-opacity 0.3s ease, stroke-width 0.3s ease",
                    ...(edge.style ?? {}),
                },
                className: isReverseFlow ? "edge-flow-reverse" : "edge-flow-forward",
            };
        });
    }, [edges, connectedNodeIds]);

    const styledNodes = useMemo(() => {
        if (!connectedNodeIds) return nodes;
        return nodes.map((node) => ({
            ...node,
            style: {
                ...node.style,
                opacity: connectedNodeIds.has(node.id) ? 1 : 0.3,
                transition: "opacity 0.3s ease",
            },
        }));
    }, [nodes, connectedNodeIds]);

    return (
        <div style={{ position: "relative", width: "100%", height: "100%" }} className="explorer-canvas" data-theme={theme.palette.mode}>
            {/* Animated line-drop background */}
            <div className="explorer-lines">
                <div className="explorer-line" />
                <div className="explorer-line" />
                <div className="explorer-line" />
                <div className="explorer-line" />
                <div className="explorer-line" />
            </div>

            <ReactFlow
                nodes={styledNodes}
                edges={styledEdges}
                nodeTypes={nodeTypes}
                onNodesChange={onNodesChange}
                fitView
                fitViewOptions={{ padding: 0.3 }}
                minZoom={0.3}
                maxZoom={1.5}
                nodesDraggable={true}
                nodesConnectable={false}
                proOptions={{ hideAttribution: true }}
            >
                <Background
                    color={theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}
                    gap={24}
                    size={1}
                />
                <Controls
                    showInteractive={false}
                    style={{
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 10,
                    }}
                />
                <MiniMap
                    nodeColor={(node) => {
                        const entityType = (node.data as ExplorerNode["data"])?.entityType;
                        return entityType ? getNodeColor(entityType) : "#9e9e9e";
                    }}
                    nodeStrokeWidth={0}
                    zoomable
                    pannable
                    position="bottom-right"
                    style={{
                        background: isDark ? "rgba(20, 20, 30, 0.85)" : "rgba(245, 245, 245, 0.9)",
                        borderRadius: 8,
                        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                    }}
                    maskColor={isDark ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.1)"}
                />
            </ReactFlow>
        </div>
    );
}
